import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "../AdminShell";
import ConfirmModal from "../ConfirmModal";
import { adminFetch } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-categories.css";

/**
 * RAdminCategories — /admin/categories
 *
 * Redesigned Category Management screen (design-ref/admin/Categories.html)
 * with functionality replicated 1:1 from the legacy
 * frontend/src/pages/AdminCategoriesPage.js:
 *   - GET /api/admin/categories on mount (auth via adminFetch — missing/
 *     expired token or 401 redirects to /admin/login)
 *   - client-side search across name + description (case-insensitive)
 *   - card grid: gem icon, name, Visible/Hidden badge (visible !== false),
 *     description, "{product_count} products", Edit + Delete
 *   - Delete disabled when product_count > 0
 *     (tooltip "Cannot delete category with products")
 *   - Add/Edit modal: Category Name* (required), Description,
 *     "Visible in store" checkbox (default checked)
 *   - POST /api/admin/categories (create) /
 *     PUT /api/admin/categories/{id} (update) with {name, description,
 *     visible}; on success: close modal, reset form, refetch
 *   - delete confirm (design's ConfirmModal replaces window.confirm) →
 *     DELETE /api/admin/categories/{id}; on success refetch
 *   - legacy inline success/error Alerts (5s auto-clear) → toast pills
 *     (auto-dismiss); error copy preserved incl. `data.detail` fallbacks
 *   - legacy "Back to Dashboard" button is superseded by the shell sidebar
 */

const GemIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="#1bab88"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3l8 7-8 11-8-11z" />
  </svg>
);

const EMPTY_FORM = { name: "", description: "", visible: true };

const RAdminCategories = () => {
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Add/Edit modal state (legacy: showModal + modalMode + editingCategory)
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Delete confirm state (replaces legacy window.confirm)
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminFetch("/api/admin/categories", { navigate });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      } else {
        toast.error("Failed to fetch categories");
      }
    } catch (err) {
      if (!err.isAuthError) {
        toast.error("Error loading categories: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingCategory(null);
  };

  const openCreateModal = () => {
    setModalMode("create");
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setModalMode("edit");
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      visible: category.visible !== false, // default to true if undefined
    });
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const handleCreateCategory = async () => {
    try {
      setSaving(true);
      const response = await adminFetch("/api/admin/categories", {
        method: "POST",
        body: JSON.stringify(formData),
        navigate,
      });
      if (response.ok) {
        toast.success("✓ Category created");
        setShowModal(false);
        resetForm();
        fetchCategories();
      } else {
        const data = await response.json();
        toast.error(
          typeof data.detail === "string"
            ? data.detail
            : "Failed to create category"
        );
      }
    } catch (err) {
      if (!err.isAuthError) {
        toast.error("Error creating category: " + err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCategory = async () => {
    try {
      setSaving(true);
      const response = await adminFetch(
        `/api/admin/categories/${editingCategory._id}`,
        {
          method: "PUT",
          body: JSON.stringify(formData),
          navigate,
        }
      );
      if (response.ok) {
        toast.success("✓ Category updated");
        setShowModal(false);
        resetForm();
        fetchCategories();
      } else {
        const data = await response.json();
        toast.error(
          typeof data.detail === "string"
            ? data.detail
            : "Failed to update category"
        );
      }
    } catch (err) {
      if (!err.isAuthError) {
        toast.error("Error updating category: " + err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (modalMode === "create") {
      handleCreateCategory();
    } else {
      handleUpdateCategory();
    }
  };

  const confirmDelete = async () => {
    if (!deletingCategory) return;
    try {
      setDeleteBusy(true);
      const response = await adminFetch(
        `/api/admin/categories/${deletingCategory._id}`,
        { method: "DELETE", navigate }
      );
      if (response.ok) {
        toast.success("Category deleted");
        fetchCategories();
      } else {
        const data = await response.json();
        toast.error(
          typeof data.detail === "string"
            ? data.detail
            : "Failed to delete category"
        );
      }
    } catch (err) {
      if (!err.isAuthError) {
        toast.error("Error deleting category: " + err.message);
      }
    } finally {
      setDeleteBusy(false);
      setDeletingCategory(null);
    }
  };

  // Client-side filter — same predicate as the legacy page.
  const filteredCategories = categories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description &&
        category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const subtitle = loading
    ? null
    : searchTerm
      ? `${filteredCategories.length} of ${categories.length} categories`
      : `${categories.length} categories`;

  return (
    <AdminShell
      active="categories"
      pageClass="pg-admin-categories"
      eyebrow="Commerce"
      title="Category Management"
      subtitle={subtitle}
    >
      {/* toolbar: search + Add Category */}
      <div className="cat-toolbar">
        <input
          className="cat-search"
          placeholder="Search categories…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search categories"
        />
        <button type="button" className="adm-btn" onClick={openCreateModal}>
          + Add Category
        </button>
      </div>

      {loading ? (
        <div className="adm-loading">
          <div className="adm-spinner" />
          Loading categories…
        </div>
      ) : filteredCategories.length === 0 ? (
        <div className="adm-empty">
          <div className="adm-empty-icon">
            <GemIcon />
          </div>
          <div className="adm-empty-title">
            {categories.length === 0
              ? "No categories found"
              : "No matching categories"}
          </div>
          <div className="adm-empty-sub">
            {categories.length === 0
              ? "Create your first category to organize your products."
              : "Try adjusting your search terms."}
          </div>
          {categories.length === 0 && (
            <button
              type="button"
              className="adm-btn cat-empty-cta"
              onClick={openCreateModal}
            >
              + Add Category
            </button>
          )}
        </div>
      ) : (
        <div className="adm-grid3">
          {filteredCategories.map((category) => {
            // Handle visible field — default to true if undefined
            const isVisible = category.visible !== false;
            const hasProducts = category.product_count > 0;
            return (
              <div key={category._id} className="cat-card">
                <div className="cat-card-head">
                  <span className="cat-icon">
                    <GemIcon />
                  </span>
                  <div className="cat-name-wrap">
                    <div className="cat-name">{category.name}</div>
                  </div>
                  <span
                    className={
                      "cat-badge" + (isVisible ? "" : " cat-badge--hidden")
                    }
                  >
                    {isVisible ? "Visible" : "Hidden"}
                  </span>
                </div>
                <p className="cat-desc">{category.description || ""}</p>
                <div className="cat-foot">
                  <span className="cat-count">
                    {category.product_count || 0} products
                  </span>
                  <div className="cat-actions">
                    <button
                      type="button"
                      className="cat-btn"
                      onClick={() => openEditModal(category)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="cat-btn cat-btn--delete"
                      disabled={hasProducts}
                      title={
                        hasProducts
                          ? "Cannot delete category with products"
                          : "Delete category"
                      }
                      onClick={() => setDeletingCategory(category)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit modal */}
      {showModal && (
        <div className="adm-modal-overlay" onClick={closeModal}>
          <div
            className="adm-modal cat-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="adm-modal-title">
              {modalMode === "edit" ? "Edit Category" : "Add Category"}
            </div>
            <form onSubmit={handleSubmit}>
              <label className="adm-field">
                <span className="adm-label">Category Name *</span>
                <input
                  className="adm-input"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g. Bracelets"
                  required
                />
              </label>
              <label className="adm-field">
                <span className="adm-label">Description</span>
                <textarea
                  className="adm-textarea cat-textarea"
                  rows={2}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Short description shown in the store"
                />
              </label>
              <label className="cat-visible">
                <input
                  type="checkbox"
                  className="adm-check"
                  checked={formData.visible}
                  onChange={(e) =>
                    setFormData({ ...formData, visible: e.target.checked })
                  }
                />
                Visible in store
              </label>
              <div className="adm-modal-actions">
                <button
                  type="button"
                  className="adm-btn adm-btn--soft"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="adm-btn" disabled={saving}>
                  {modalMode === "edit" ? "Save Changes" : "Create Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm (design pattern; same DELETE call as legacy) */}
      <ConfirmModal
        open={!!deletingCategory}
        title="Delete this category?"
        body="Categories containing products cannot be deleted. This cannot be undone."
        confirmLabel="Delete"
        danger
        busy={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={() => setDeletingCategory(null)}
      />
    </AdminShell>
  );
};

export default RAdminCategories;
